import apiClient from './client';
import type {
  AuthoredAgendaSectionForCoach,
  CoachSessionLeaderNote,
} from './coachSessionPrep';

/**
 * MED-792 / 2.3a — Coach Active Session API client.
 *
 * Mirrors medley-api/types/coachActiveSession.ts. Backend Date columns
 * serialize to ISO 8601 strings over the wire.
 *
 * Per Decision A2 (auto-story 2026-05-14), AC3 push-to-participants is
 * deferred — these endpoints persist coach-side state and emit a Node
 * domain event but do not push.
 *
 * AC7 AI disclosure modal gates on `ai_features_enabled && ai_listening_enabled`.
 * Decision B1 originally specified `ai_features_enabled` alone, but the modal's
 * copy ("AI listening is active for this session" + a description of recording
 * behavior) is materially false when the per-session listening toggle is off.
 * MED-876 introduced the toggle; this gate was tightened post-QA to match.
 */

// ---------------------------------------------------------------------------
// Type mirrors
// ---------------------------------------------------------------------------

export type SessionStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'rescheduled';

export interface ActiveSessionParticipant {
  user_id: string;
  display_name: string;
  initials: string;
  is_connected: boolean;
  /**
   * MED-1132 — true when this participant has a saved, non-empty commitment for
   * the session. Powers the facilitator commitment-progress summary. Content-free:
   * the backend never sends the commitment text itself (AC4). Reflects current
   * state — clearing + re-saving an empty commitment flips this back to false (AC6).
   */
  has_logged_commitment: boolean;
}

export interface ActiveSessionSlideDeck {
  id: string;
  converted_pdf_s3_key: string | null;
  processing_state: 'pending' | 'processing' | 'ready' | 'failed';
  original_filename: string;
}

export type SectionTimeSeconds = Record<string, number>;

export interface ActiveSessionState {
  session_id: string;
  title: string;
  session_number: number | null;
  status: SessionStatus;
  duration_minutes: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  group_id: string | null;
  group_index: number | null;
  group_name: string | null;
  cohort_id: string;
  cohort_name: string;
  organization_name: string | null;
  /**
   * MED-1103 / MED-1181 — the cohort's group-coaching per-modality Live Session
   * Delivery toggle; `false` when there is no active group_coaching modality.
   * MED-1181 uses it as the page-level LSD gate (defense-in-depth against
   * direct-URL access) so the active-session shell fails closed for a classic
   * MED01 cohort.
   */
  live_session_delivery_enabled: boolean;
  /**
   * MED-1112 — resolved meeting link (Zoom/Teams/Meet), or null when there's
   * none (in-person). Surfaced as the header "Join video call" link.
   */
  meeting_link: string | null;
  modality_id: string | null;
  coaching_sub_type: 'coach_led' | 'peer_led' | null;
  /** Modality-level AI gate (MED-848). Required for any AI surface to exist. */
  ai_features_enabled: boolean;
  /**
   * Group-level AI Listening toggle (MED-876 AC3). Both the AC7 disclosure
   * modal and the MED-872 AC3/AC6 in-session AI surface require
   * `ai_features_enabled && ai_listening_enabled` (the modality AI flag alone
   * is not sufficient — listening must actually be on for THIS session).
   */
  ai_listening_enabled: boolean;
  /**
   * MED-1004 (E4.6a) — true iff the current viewer is the designated leader of
   * this peer-led session. Gates whether the peer leader sees the Pause AI
   * control (coaches see it via the coach route). Presentational only: the
   * authoritative pause/resume authz is enforced server-side in
   * session-intelligence against sessions.session_leader_id.
   */
  is_session_leader: boolean;
  /**
   * MED-976 (E1.3h) — Authored sections (e1.3d/e/f model) replace the legacy
   * parsed_outline. The coach active route is already gated behind
   * `live_session_delivery` (MED-886) so this field is always the authored
   * shape — no legacy fallback at the type level.
   */
  sections: AuthoredAgendaSectionForCoach[];
  slide_deck: ActiveSessionSlideDeck | null;
  leader_notes: CoachSessionLeaderNote[];
  current_section_index: number | null;
  /**
   * MED-976/E3.3a — coach-selected "main" slide for the shared whiteboard
   * (1-based PDF page). NULL ⇒ the current section's first associated slide.
   */
  current_slide_page: number | null;
  current_section_entered_at: string | null;
  section_time_seconds: SectionTimeSeconds;
  coach_session_notes_html: string;
  coach_session_notes_updated_at: string | null;
  /**
   * MED-952 — true when any assigned coach is currently present. On the coach's
   * own view this is effectively always true while polling; it primarily drives
   * the participant absence notice.
   */
  coach_present: boolean;
  participants: ActiveSessionParticipant[];
  /**
   * MED-1016 — cohort-level commitment gate (cohorts.commitment_tracking_enabled),
   * surfaced so the coach Whiteboard's Commitment card mirrors the participant gate.
   */
  commitment_tracking_enabled: boolean;
  /**
   * MED-1016 AC4 — the REQUESTING user's prior-session commitment text, mirroring
   * the participant payload. Populated only for a peer leader (a participant)
   * when the gate + section's show_last_session_commitment are on and a prior
   * commitment exists; null for coaches (no personal commitment) otherwise.
   */
  last_session_commitment: string | null;
  /**
   * MED-1013 / MED-1029 — prep-selected "In Use" AI prompt segments surfaced for
   * the in-session Session Guide (Purpose / Opening Prompt / If Group Stalls).
   * Optional + forward-compatible: MED-1029 (backend) populates it; until then
   * it is absent and the Guide renders no In-Use cards (MED-1013 AC5).
   */
  ai_prompt_selections?: ActiveSessionAiPromptSelection[];
}

/** MED-1013 / MED-1029 — one prep-selected "In Use" AI prompt for the Guide. */
export interface ActiveSessionAiPromptSelection {
  /** Segment key, e.g. 'purpose' | 'opening_prompt' | 'stall_recovery'. */
  segment: string;
  /** Display label: "Purpose" | "Opening Prompt" | "If Group Stalls". */
  label: string;
  /** Prompt body text. */
  body: string;
}

/** MED-952 — response from the presence heartbeat. */
export interface CoachPresenceResult {
  status: SessionStatus;
  coach_present: boolean;
}

export interface UpdateCurrentSectionRequest {
  section_index: number;
}

export interface UpdateCoachNotesRequest {
  content_html: string;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const coachActiveSessionApi = {
  /** GET composite payload for the Active Session screen. */
  getActiveState: (sessionId: string): Promise<ActiveSessionState> =>
    apiClient.get<ActiveSessionState>(
      `/api/coach/sessions/${sessionId}/active-state`
    ),

  /**
   * GET read-only composite payload for the Active Session Preview screen
   * (MED-873 / Story 2.3c). Same response shape as `getActiveState` but the
   * call is guaranteed to be side-effect free — no state transitions, no
   * domain events, no participant notifications.
   */
  getPreviewState: (sessionId: string): Promise<ActiveSessionState> =>
    apiClient.get<ActiveSessionState>(
      `/api/coach/sessions/${sessionId}/preview-state`
    ),

  /** POST scheduled → in_progress (T-15 gate enforced server-side). */
  startSession: (sessionId: string): Promise<ActiveSessionState> =>
    apiClient.post<ActiveSessionState>(
      `/api/coach/sessions/${sessionId}/start`
    ),

  /** POST in_progress → completed. */
  endSession: (sessionId: string): Promise<ActiveSessionState> =>
    apiClient.post<ActiveSessionState>(`/api/coach/sessions/${sessionId}/end`),

  /**
   * POST presence heartbeat (MED-952). Marks the coach present on the active
   * session screen and closes any open absence interval. Polled ~every 4s while
   * the session is in_progress. Returns the live status so the client can
   * detect an auto-end / external end and navigate to post-session.
   */
  recordPresence: (sessionId: string): Promise<CoachPresenceResult> =>
    apiClient.post<CoachPresenceResult>(
      `/api/coach/sessions/${sessionId}/presence`
    ),

  /** PATCH current section (advance / retreat / jump). */
  updateCurrentSection: (
    sessionId: string,
    body: UpdateCurrentSectionRequest
  ): Promise<ActiveSessionState> =>
    apiClient.patch<ActiveSessionState>(
      `/api/coach/sessions/${sessionId}/current-section`,
      body
    ),

  /**
   * PATCH the "main" slide shown on the shared whiteboard (MED-976/E3.3a).
   * `slide_page` must be one of the current section's associated slides; pass
   * null to clear (falls back to the section's first slide).
   */
  updateCurrentSlide: (
    sessionId: string,
    body: { slide_page: number | null }
  ): Promise<ActiveSessionState> =>
    apiClient.patch<ActiveSessionState>(
      `/api/coach/sessions/${sessionId}/current-slide`,
      body
    ),

  /** PATCH coach in-session notes. Sanitized server-side. */
  updateCoachNotes: (
    sessionId: string,
    body: UpdateCoachNotesRequest
  ): Promise<{ updated_at: string }> =>
    apiClient.patch<{ updated_at: string }>(
      `/api/coach/sessions/${sessionId}/coach-notes`,
      body
    ),
};

// ---------------------------------------------------------------------------
// MED-1105 — dev-only session debug (bot-join trace + force re-join)
// ---------------------------------------------------------------------------

/**
 * One hop of the bot-join path, normalized across the two backends that emit
 * traces: medley-api dispatch decisions (this REST surface) and
 * session-intelligence (live over the in-session socket). The drawer merges
 * both by `ts` into one timeline.
 */
export interface DebugTraceEvent {
  /** ISO-8601 emit time. */
  ts: string;
  source: 'medley-api' | 'session-intelligence' | 'recall';
  /** e.g. 'dispatch.gate' | 'dispatch.post' | 'recall.request' | 'bot.status_change'. */
  hop: string;
  status: 'ok' | 'skip' | 'error';
  detail: string;
  data?: Record<string, unknown>;
}

/**
 * MED-1210 — one "Generate Recommendation" (read-the-room) diagnostic trace for
 * the `?debug=1` console: WHY a click did or didn't surface a prompt, with the
 * request/response detail. Delivered point-to-point to the requesting leader's
 * socket only (`read_the_room_trace`); held in client state, never persisted.
 * `judge` is filled in later from the sampled async `read_the_room_judge` event.
 */
export interface ReadTheRoomTraceEntry {
  traceId: string;
  ts: string;
  sessionId: string;
  outcome: 'ok' | 'no_read' | 'error';
  cause:
    | 'ok'
    | 'paused'
    | 'insufficient_transcript'
    | 'no_confident_read'
    /** v3 — honest exhaustion: every live thread already pulled. */
    | 'nothing_new'
    /** MED-1273 — the model named a participant in the spoken prompt somewhere
     *  it could not be removed without breaking the sentence, so the read was
     *  refused. The leader sees the ordinary calm empty state. */
    | 'name_unrepairable'
    | 'no_active_session'
    | 'not_primed'
    | 'normalize_error'
    | 'model_timeout_or_empty';
  detail: string;
  latencyMs: number;
  window?: { lines: number; chars: number };
  request?: {
    recipe: { name: string; version: string; model: string; temperature: number; maxTokens: number };
    systemPrompt: string;
    context: unknown;
  };
  response?: {
    decision: string;
    noticing: string;
    suggested_prompt: string;
    why: string;
    decision_confidence: number;
  };
  /** v3 — anchor containment verdict. */
  anchor?: 'ok' | 'stripped_ungrounded';
  /** MED-1273 — spoken-prompt name-guard verdict (count only; names are PII). */
  spokenNames?: { verdict: 'clean' | 'repaired' | 'unrepairable'; count: number };
  judgeSampled: boolean;
  judge?: { score: number; critical_issues: string[]; narrative: string };
}

/** MED-1210 — the sampled judge verdict, correlated to a trace by traceId. */
export interface ReadTheRoomJudgeUpdate {
  traceId: string;
  sessionId: string;
  verdict: { score: number; critical_issues: string[]; narrative: string };
}

/**
 * MED-1245 (Tier 1) — per-window rationale for the AUTOMATIC nudge path in the
 * ?debug=1 console: why the AI nudged or stayed silent, and what became of the
 * decision. Leader-scoped on the wire (SI emits to the `${sessionId}:leaders`
 * room only) because `why` can reference the transcript window.
 */
export interface InferenceDetailEntry {
  ts: string;
  sessionId: string;
  decision: 'nudge' | 'none';
  outcome: 'surfaced' | 'none' | 'suppressed_cooldown' | 'suppressed_empty';
  why: string;
  decisionConfidence: number;
  nudgeType: string;
  message: string;
  nextEligibleInSec?: number;
}

/**
 * MED-1281 — one synthetic transcript line to push into a LIVE session through
 * the same callback the Recall webhook drives, so it exercises the whole chain
 * (window → trigger selection → recipe → judge → cooldown → nudge).
 *
 * `gapSeconds` is simulated silence BEFORE this line: session-intelligence
 * back-dates the previously buffered line so `computeRecentSilence` sees a real
 * lull. It is the only way to make `reflect_silence` reachable without waiting
 * out the clock. Keep it under the 5-minute transcript window — back-dating the
 * preceding line past the window evicts it and collapses the gap to zero.
 */
export interface InjectTranscriptLine {
  speaker: string;
  text: string;
  gapSeconds?: number;
}

/** medley-api dispatch-trace wire shape (no `source` — always medley-api). */
interface DispatchTraceEventWire {
  ts: string;
  hop: string;
  status: 'ok' | 'skip' | 'error';
  detail: string;
  data?: Record<string, unknown>;
}

const toDebugTraceEvent = (e: DispatchTraceEventWire): DebugTraceEvent => ({
  ...e,
  source: 'medley-api',
});

export const sessionDebugApi = {
  /** GET the medley-api dispatch trace (which gate skipped, or the SI POST result). */
  getDispatchTrace: async (sessionId: string): Promise<DebugTraceEvent[]> => {
    const { trace } = await apiClient.get<{ trace: DispatchTraceEventWire[] }>(
      `/api/coach/session-debug/${sessionId}/dispatch-trace`
    );
    return trace.map(toDebugTraceEvent);
  },

  /**
   * POST re-fire the session-intelligence bot-join dispatch for this session and
   * return the fresh dispatch trace. Internal + non-production only (server-gated);
   * spawns a real Recall bot, so it is disabled in prod.
   */
  forceRejoin: async (sessionId: string): Promise<DebugTraceEvent[]> => {
    const { trace } = await apiClient.post<{ trace: DispatchTraceEventWire[] }>(
      `/api/coach/session-debug/${sessionId}/force-rejoin`
    );
    return trace.map(toDebugTraceEvent);
  },

  /**
   * MED-1281 — POST synthetic transcript lines into a live session.
   *
   * Deliberately routed through medley-api (NOT the drawer's direct-to-SI
   * `siPost` helper): medley-api is the only layer that authorizes per USER —
   * coach, admin, or the designated peer leader for THIS session. SI's own env
   * flag (`ALLOW_TRANSCRIPT_INJECTION`, absent in production → 404) is the
   * second gate; neither is sufficient alone.
   */
  injectTranscript: async (
    sessionId: string,
    lines: InjectTranscriptLine[]
  ): Promise<{ injected: number; sessionId: string }> =>
    apiClient.post<{ injected: number; sessionId: string }>(
      `/api/coach/session-debug/${sessionId}/inject-transcript`,
      { lines }
    ),
};
